import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ApiClient, {
  AnalyticsOverview,
  AnalyticsTrends,
  AnalyticsForms,
  AnalyticsSpamAccuracy,
  AnalyticsGeo,
  AnalyticsBrowsers,
  Form,
} from '../api/client';
import { LocalTabs, PageHeader } from './ui';

interface AnalyticsProps {
  apiClient: ApiClient;
}

const PERIOD_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last 12 months' },
];

type FormSortField = 'name' | 'total' | 'approved' | 'spam' | 'approvalRate';
type SortDirection = 'asc' | 'desc';
type AnalyticsTab = 'overview' | 'forms' | 'traffic';
type TrendBucket = {
  label: string;
  total: number;
  approved: number;
  flagged: number;
  spam: number;
  other: number;
};

export const formatTrendDate = (date: string, options: Intl.DateTimeFormatOptions) =>
  new Date(`${date}T00:00:00`).toLocaleDateString(undefined, options);

export const buildTrendBuckets = (source: AnalyticsTrends, days: number): TrendBucket[] => {
  if (source.length === 0) return [];

  if (days <= 30) {
    return source.map((day) => ({
      label: formatTrendDate(day.date, { month: 'short', day: 'numeric' }),
      total: day.total,
      approved: day.approved,
      flagged: day.flagged,
      spam: day.spam,
      other: Math.max(day.total - day.approved - day.flagged - day.spam, 0),
    }));
  }

  if (days >= 365) {
    const monthMap = new Map<string, TrendBucket>();
    source.forEach((day) => {
      const key = day.date.slice(0, 7);
      const existing = monthMap.get(key);
      if (existing) {
        existing.total += day.total;
        existing.approved += day.approved;
        existing.flagged += day.flagged;
        existing.spam += day.spam;
        existing.other += Math.max(day.total - day.approved - day.flagged - day.spam, 0);
      } else {
        monthMap.set(key, {
          label: formatTrendDate(`${key}-01`, { month: 'short', year: 'numeric' }),
          total: day.total,
          approved: day.approved,
          flagged: day.flagged,
          spam: day.spam,
          other: Math.max(day.total - day.approved - day.flagged - day.spam, 0),
        });
      }
    });
    return Array.from(monthMap.values());
  }

  const buckets: TrendBucket[] = [];
  for (let i = 0; i < source.length; i += 7) {
    const week = source.slice(i, i + 7);
    const start = week[0].date;
    const end = week[week.length - 1].date;
    buckets.push({
      label: `${formatTrendDate(start, { month: 'short', day: 'numeric' })}-${formatTrendDate(end, { day: 'numeric' })}`,
      total: week.reduce((sum, day) => sum + day.total, 0),
      approved: week.reduce((sum, day) => sum + day.approved, 0),
      flagged: week.reduce((sum, day) => sum + day.flagged, 0),
      spam: week.reduce((sum, day) => sum + day.spam, 0),
      other: week.reduce((sum, day) => sum + Math.max(day.total - day.approved - day.flagged - day.spam, 0), 0),
    });
  }
  return buckets;
};

export const calculateTrendPeriodTotals = (source: AnalyticsTrends) => {
  const totals = source.reduce(
    (acc, day) => {
      acc.total += day.total;
      acc.approved += day.approved;
      acc.flagged += day.flagged;
      acc.spam += day.spam;
      return acc;
    },
    { total: 0, approved: 0, flagged: 0, spam: 0 },
  );
  const other = Math.max(totals.total - totals.approved - totals.flagged - totals.spam, 0);
  return {
    ...totals,
    other,
    approvalRate: totals.total > 0 ? totals.approved / totals.total : 0,
    flaggedRate: totals.total > 0 ? totals.flagged / totals.total : 0,
    spamRate: totals.total > 0 ? totals.spam / totals.total : 0,
    otherRate: totals.total > 0 ? other / totals.total : 0,
  };
};

const Analytics: React.FC<AnalyticsProps> = ({ apiClient }) => {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [trends, setTrends] = useState<AnalyticsTrends | null>(null);
  const [forms, setForms] = useState<AnalyticsForms | null>(null);
  const [spamAccuracy, setSpamAccuracy] = useState<AnalyticsSpamAccuracy | null>(null);
  const [geo, setGeo] = useState<AnalyticsGeo | null>(null);
  const [browsers, setBrowsers] = useState<AnalyticsBrowsers | null>(null);
  const [formNames, setFormNames] = useState<Record<string, { name: string; domain: string }>>({});
  const [formSortField, setFormSortField] = useState<FormSortField>('total');
  const [formSortDirection, setFormSortDirection] = useState<SortDirection>('desc');
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('overview');

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewData, trendsData, formsData, spamData, geoData, browsersData, formListData] = await Promise.all([
        apiClient.getAnalyticsOverview(days),
        apiClient.getAnalyticsTrends(days),
        apiClient.getAnalyticsForms(days),
        apiClient.getAnalyticsSpamAccuracy(days),
        apiClient.getAnalyticsGeo(days),
        apiClient.getAnalyticsBrowsers(days),
        apiClient.listForms(),
      ]);
      setOverview(overviewData);
      setTrends(trendsData);
      setForms(formsData);
      setSpamAccuracy(spamData);
      setGeo(geoData);
      setBrowsers(browsersData);
      setFormNames(
        (Array.isArray(formListData) ? formListData : []).reduce<Record<string, { name: string; domain: string }>>(
          (acc, form: Form) => {
            acc[form.hash] = { name: form.name, domain: form.domain };
            return acc;
          },
          {},
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [apiClient, days]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`;

  const trendBuckets = useMemo<TrendBucket[]>(() => {
    return buildTrendBuckets(trends || [], days);
  }, [trends, days]);

  const maxTrendTotal = useMemo(() => {
    return Math.max(...trendBuckets.map((bucket) => bucket.total), 1);
  }, [trendBuckets]);

  const periodTotals = useMemo(() => {
    return calculateTrendPeriodTotals(trends || []);
  }, [trends]);

  const sortedForms = useMemo(() => {
    const list = [...(forms || [])];
    list.sort((a, b) => {
      let result = 0;
      if (formSortField === 'name') {
        const aName = formNames[a.formHash]?.name || a.formHash;
        const bName = formNames[b.formHash]?.name || b.formHash;
        result = aName.localeCompare(bName);
      } else {
        result = a[formSortField] - b[formSortField];
      }
      return formSortDirection === 'asc' ? result : -result;
    });
    return list;
  }, [forms, formNames, formSortField, formSortDirection]);

  const handleFormSort = (field: FormSortField) => {
    if (formSortField === field) {
      setFormSortDirection(formSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setFormSortField(field);
      setFormSortDirection(field === 'name' ? 'asc' : 'desc');
    }
  };

  const sortIndicator = (field: FormSortField) => {
    if (formSortField !== field) return '↕';
    return formSortDirection === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div className="manager-page analytics-page">
        <div className="empty-state">
        <div className="loading-spinner"></div>
        <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manager-page analytics-page">
        <div className="alert alert-error">
          <span>Warning</span>
          <span>{error}</span>
          <button className="btn btn-sm btn-secondary" onClick={loadAnalytics} style={{ marginLeft: 'auto' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="manager-page analytics-page">
      <PageHeader
        title="Analytics"
        actions={
          <>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}>
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={loadAnalytics}>
            Refresh
          </button>
          </>
        }
      />

      <div className="analytics-shell">
        <LocalTabs
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'forms', label: 'Forms' },
            { id: 'traffic', label: 'Traffic' },
          ]}
          active={activeTab}
          onChange={setActiveTab}
        />

        <div className="analytics-tab-panel">
          {activeTab === 'overview' && (
            <>
              {overview && (
        <>
          <section className="analytics-summary-panel">
            <div className="metric-grid analytics-summary-metrics">
            <MetricCard label="Total submissions" value={periodTotals.total} tone="brand" />
            <MetricCard label="Approved" value={periodTotals.approved} rate={formatPercent(periodTotals.approvalRate)} tone="success" />
            <MetricCard label="Flagged" value={periodTotals.flagged} rate={formatPercent(periodTotals.flaggedRate)} tone="warning" />
            <MetricCard label="Spam" value={periodTotals.spam} rate={formatPercent(periodTotals.spamRate)} tone="danger" />
            <MetricCard label="Other" value={periodTotals.other} rate={formatPercent(periodTotals.otherRate)} tone="muted" />
            </div>
          </section>
        </>
              )}

              {trendBuckets.length > 0 && (
        <section className="card analytics-card">
          <div className="card-header">
            <h3>Submission Trends</h3>
            <span className="analytics-caption">
              {days >= 365 ? 'Grouped by month' : days > 30 ? 'Grouped by week' : `${days} day window`}
            </span>
          </div>
          <div className="trend-chart">
            {trendBuckets.map((bucket) => {
              const height = Math.max((bucket.total / maxTrendTotal) * 180, bucket.total > 0 ? 4 : 2);
              const approvedPct = bucket.total > 0 ? (bucket.approved / bucket.total) * 100 : 0;
              const flaggedPct = bucket.total > 0 ? (bucket.flagged / bucket.total) * 100 : 0;
              const spamPct = bucket.total > 0 ? (bucket.spam / bucket.total) * 100 : 0;
              const flaggedStart = approvedPct;
              const spamStart = approvedPct + flaggedPct;
              const otherStart = approvedPct + flaggedPct + spamPct;
              return (
                <div className="trend-day" key={bucket.label}>
                  <div
                    className="trend-bar"
                    style={{
                      height,
                      background: bucket.total > 0
                        ? `linear-gradient(to top,
                          var(--success) 0%,
                          var(--success) ${approvedPct}%,
                          #eab308 ${flaggedStart}%,
                          #eab308 ${spamStart}%,
                          var(--danger) ${spamStart}%,
                          var(--danger) ${otherStart}%,
                          var(--brand-500) ${otherStart}%,
                          var(--brand-500) 100%)`
                        : 'var(--brand-100)',
                    }}
                    title={`${bucket.label}: ${bucket.total} submissions`}
                  />
                </div>
              );
            })}
          </div>
          <div className="trend-axis" style={{ gridTemplateColumns: `repeat(${trendBuckets.length}, minmax(0, 1fr))` }}>
            {trendBuckets.map((bucket, index) => {
              const labelEvery = Math.max(Math.ceil(trendBuckets.length / 6), 1);
              const showLabel = days >= 365 || index === 0 || index === trendBuckets.length - 1 || index % labelEvery === 0;
              return (
                <span key={bucket.label} style={{ visibility: showLabel ? 'visible' : 'hidden' }}>
                  {bucket.label}
                </span>
              );
            })}
          </div>
          <div className="analytics-legend">
            <LegendItem color="var(--success)" label="Approved" />
            <LegendItem color="#eab308" label="Flagged" />
            <LegendItem color="var(--danger)" label="Spam" />
            <LegendItem color="var(--brand-500)" label="Other" />
          </div>
        </section>
              )}

              {spamAccuracy && (
                <section className="analytics-quality-panel">
                  <div>
                    <div className="analytics-inline-header">
                      <h3>Spam score distribution</h3>
                    </div>
                    <div className="risk-grid">
                      <MiniMetric label="Low" value={spamAccuracy.scoreDistribution.low} tone="success" />
                      <MiniMetric label="Medium" value={spamAccuracy.scoreDistribution.medium} tone="warning" />
                      <MiniMetric label="High" value={spamAccuracy.scoreDistribution.high} tone="danger" />
                    </div>
                  </div>

                  <div>
                    <div className="analytics-inline-header">
                      <h3>Detection accuracy</h3>
                      <span>{spamAccuracy.detection.totalProcessed.toLocaleString()} processed</span>
                    </div>
                    <div className="analytics-stack">
                      <div className="risk-grid two">
                        <MiniMetric
                          label={`False positives ${formatPercent(spamAccuracy.accuracy.falsePositiveRate)}`}
                          value={spamAccuracy.accuracy.falsePositives}
                          tone="danger"
                        />
                        <MiniMetric
                          label={`False negatives ${formatPercent(spamAccuracy.accuracy.falseNegativeRate)}`}
                          value={spamAccuracy.accuracy.falseNegatives}
                          tone="warning"
                        />
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </>
          )}

          {activeTab === 'forms' && (
            <>
              {sortedForms.length > 0 ? (
          <div className="card analytics-card">
            <div className="card-header">
              <h3>Per-Form Performance</h3>
            </div>
            <div className="data-table-wrap analytics-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>
                      <button className="table-sort-button" onClick={() => handleFormSort('name')}>
                        Form {sortIndicator('name')}
                      </button>
                    </th>
                    <th style={{ textAlign: 'right' }}>
                      <button className="table-sort-button right" onClick={() => handleFormSort('total')}>
                        Total {sortIndicator('total')}
                      </button>
                    </th>
                    <th style={{ textAlign: 'right' }}>
                      <button className="table-sort-button right" onClick={() => handleFormSort('approved')}>
                        Approved {sortIndicator('approved')}
                      </button>
                    </th>
                    <th style={{ textAlign: 'right' }}>
                      <button className="table-sort-button right" onClick={() => handleFormSort('spam')}>
                        Spam {sortIndicator('spam')}
                      </button>
                    </th>
                    <th style={{ textAlign: 'right' }}>
                      <button className="table-sort-button right" onClick={() => handleFormSort('approvalRate')}>
                        Approval Rate {sortIndicator('approvalRate')}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedForms.map((form) => (
                    <tr key={form.formHash}>
                      <td>
                        <div className="analytics-form-name">
                          <strong>{formNames[form.formHash]?.name || form.formHash}</strong>
                          <span>
                            {formNames[form.formHash]?.domain || form.formHash}
                          </span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>{form.total}</td>
                      <td style={{ textAlign: 'right', color: 'var(--success)' }}>{form.approved}</td>
                      <td style={{ textAlign: 'right', color: 'var(--danger)' }}>{form.spam}</td>
                      <td style={{ textAlign: 'right' }}>{formatPercent(form.approvalRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
              ) : (
                <div className="empty-state">
                  <h3>No form analytics</h3>
                  <p>No form activity was found for this period.</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'traffic' && (
            <section className="analytics-two-col">
              {geo && geo.length > 0 ? (
          <DistributionCard
            title="Geographic Distribution"
            items={geo.slice(0, 10).map((country) => ({
              label: country.country,
              count: country.count,
              percentage: country.percentage,
            }))}
            formatPercent={formatPercent}
          />
              ) : (
                <div className="card analytics-card">
                  <div className="empty-state">
                    <h3>No geographic data</h3>
                    <p>No country distribution was found for this period.</p>
                  </div>
                </div>
              )}

              {browsers && (
          <div className="card analytics-card">
            <div className="card-header">
              <h3>Browsers & Devices</h3>
            </div>
            <div className="analytics-stack">
              <BrowserSection title="Top Browsers" items={browsers.browsers} formatPercent={formatPercent} />
              <BrowserSection title="Platforms" items={browsers.platforms} formatPercent={formatPercent} />
              <BrowserSection title="Devices" items={browsers.devices} formatPercent={formatPercent} />
              {(!browsers.browsers?.length && !browsers.platforms?.length && !browsers.devices?.length) && (
                <div className="empty-state">No browser data available</div>
              )}
            </div>
          </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

function MetricCard({
  label,
  value,
  rate,
  tone,
}: {
  label: string;
  value: number;
  rate?: string;
  tone: 'brand' | 'success' | 'warning' | 'danger' | 'muted';
}) {
  return (
    <div className={`metric-card ${tone}`}>
      <span>{label}</span>
      <strong>{value.toLocaleString()}</strong>
      {rate && <small>{rate} of total</small>}
    </div>
  );
}

function MiniMetric({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'success' | 'warning' | 'danger';
}) {
  return (
    <div className={`mini-metric ${tone}`}>
      <strong>{value.toLocaleString()}</strong>
      <span>{label}</span>
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span>
      <i style={{ background: color }} />
      {label}
    </span>
  );
}

function DistributionCard({
  title,
  items,
  formatPercent,
}: {
  title: string;
  items: { label: string; count: number; percentage: number }[];
  formatPercent: (value: number) => string;
}) {
  return (
    <div className="card analytics-card">
      <div className="card-header">
        <h3>{title}</h3>
      </div>
      <div className="analytics-stack">
        {items.map((item) => (
          <div className="distribution-row" key={item.label}>
            <div>
              <span>{item.label}</span>
              <small>{item.count.toLocaleString()} ({formatPercent(item.percentage)})</small>
            </div>
            <div className="distribution-track">
              <div style={{ width: `${Math.min(item.percentage * 100, 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BrowserSection({
  title,
  items,
  formatPercent,
}: {
  title: string;
  items?: { name: string; count: number; percentage: number }[];
  formatPercent: (value: number) => string;
}) {
  if (!items?.length) return null;

  return (
    <div>
      <p className="analytics-section-label">{title}</p>
      <div className="browser-list">
        {items.slice(0, 5).map((item) => (
          <div key={item.name}>
            <span>{item.name}</span>
            <small>{item.count.toLocaleString()} ({formatPercent(item.percentage)})</small>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Analytics;
